/**
 * Procedurally synthesised drum kits — rendered in-memory at first request.
 * No network. Same return shape as the CDN-backed `loadDrumKit`:
 *   { kick, snare, hihat, tom1, tom2, tom3 } each an AudioBuffer (stereo, ~1s).
 *
 * iter-138 — params are now first-class. Each kit is a flat
 * `{kick:{…}, snare:{…}, hihat:{…}, tom:{…}}` struct that the renderer
 * walks directly, instead of switch-on-kitId. This unlocks user-tunable
 * custom kits without duplicating the DSP code per kit.
 *
 * Built-ins (`synth-808` … `synth-disco`) live in `SYNTH_KIT_PRESETS`.
 * Custom user kits (saved via `lib/customDrumKits.js`) walk the same
 * pipeline by passing their own params.
 */

const cache = new Map(); // key: `${cacheKey}@${sampleRate}` → Promise<{kick,snare,...}>

const TAU = Math.PI * 2;

// ─── helpers ────────────────────────────────────────────────────────────────

function makeBuffer(ctx, durationSec) {
  const sr = ctx.sampleRate;
  const len = Math.floor(durationSec * sr);
  return { buf: ctx.createBuffer(2, len, sr), len, sr };
}

function envDecay(t, decay) { return Math.exp(-t / decay); }
function n() { return Math.random() * 2 - 1; }
function sat(x, drive = 1) { const xd = x * drive; return xd / (1 + Math.abs(xd)); }
function lp(prev, x, alpha) { return prev + alpha * (x - prev); }
function hp(prev, prevX, x, alpha) { return alpha * (prev + x - prevX); }

// ─── voice renderers (param-driven) ────────────────────────────────────────

function renderKick(ctx, p) {
  // p: { f0, fEnd, decay, click, drive, lpStrength }
  const totalSec = Math.max(0.4, p.decay * 1.6 + 0.1);
  const { buf, len, sr } = makeBuffer(ctx, totalSec);
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);

  let phase = 0;
  let lpState = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    const e = envDecay(t, p.decay);
    const pitchEnv = Math.exp(-t * 30);
    const freq = p.fEnd + (p.f0 - p.fEnd) * pitchEnv;
    phase += TAU * freq / sr;
    let s = Math.sin(phase) * e;
    if (i < 0.005 * sr) s += n() * p.click * (1 - i / (0.005 * sr));
    if (p.lpStrength > 0) {
      lpState = lp(lpState, s, p.lpStrength);
      s = lpState;
    }
    if (p.drive > 1) s = sat(s, p.drive);
    L[i] = s * 0.9;
    R[i] = s * 0.9;
  }
  return buf;
}

function renderSnare(ctx, p) {
  // p: { body, noiseMix, decay, hpAlpha, drive }
  const { buf, len, sr } = makeBuffer(ctx, 0.4);
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);

  let phase = 0;
  let prevX = 0, prevH = 0, prevHR = 0, prevXR = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    const e = envDecay(t, p.decay);
    phase += TAU * p.body / sr;
    const tone = Math.sin(phase) * e * (1 - p.noiseMix);
    const noise = n();
    const hpOut = hp(prevH, prevX, noise, p.hpAlpha);
    prevH = hpOut; prevX = noise;
    const noisePart = hpOut * e * p.noiseMix;
    let s = tone + noisePart;
    if (p.drive > 1) s = sat(s, p.drive);

    // R-channel decorrelation
    const noiseR = n();
    const hpOutR = hp(prevHR, prevXR, noiseR, p.hpAlpha);
    prevHR = hpOutR; prevXR = noiseR;
    let sR = tone + hpOutR * e * p.noiseMix;
    if (p.drive > 1) sR = sat(sR, p.drive);

    L[i] = s * 0.85;
    R[i] = sR * 0.85;
  }
  return buf;
}

function renderHihat(ctx, p, open = false) {
  // p: { decay, decayOpen, hpAlpha, ringMix }
  const decay = open ? p.decayOpen : p.decay;
  const { buf, len, sr } = makeBuffer(ctx, decay * 2 + 0.05);
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);

  const ringFreqs = [2400, 3010, 3700, 4900, 6300, 7400];
  const phases = ringFreqs.map(() => 0);

  let prevX = 0, prevH = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    const e = envDecay(t, decay);
    const noise = n();
    const hpOut = hp(prevH, prevX, noise, p.hpAlpha);
    prevH = hpOut; prevX = noise;
    let ring = 0;
    for (let r = 0; r < ringFreqs.length; r++) {
      phases[r] += TAU * ringFreqs[r] / sr;
      ring += Math.sign(Math.sin(phases[r]));
    }
    ring *= (1 / ringFreqs.length);
    const s = (hpOut * (1 - p.ringMix) + ring * p.ringMix) * e;
    L[i] = s * 0.6;
    R[i] = s * 0.6;
  }
  return buf;
}

function renderTom(ctx, p, pitchIdx) {
  // p: { decay, drive, lpStrength }
  const baseHz = pitchIdx === 1 ? 220 : pitchIdx === 2 ? 165 : 110;
  const { buf, len, sr } = makeBuffer(ctx, 0.55);
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);

  let phase = 0;
  let lpState = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    const e = envDecay(t, p.decay);
    const pitchEnv = Math.exp(-t * 6);
    const freq = baseHz * (1 + pitchEnv * 0.4);
    phase += TAU * freq / sr;
    let s = Math.sin(phase) * e;
    if (i < 0.003 * sr) s += n() * 0.25 * (1 - i / (0.003 * sr));
    if (p.lpStrength > 0) {
      lpState = lp(lpState, s, p.lpStrength);
      s = lpState;
    }
    if (p.drive > 1) s = sat(s, p.drive);
    L[i] = s * 0.85;
    R[i] = s * 0.85;
  }
  return buf;
}

// ─── kit presets ───────────────────────────────────────────────────────────

/** Param schema — every synth kit must conform. Used by the Customize panel
 *  to render sliders + by `renderKitWithParams` to drive the DSP. */
export const KIT_PARAM_SCHEMA = {
  kick:  { f0: [40, 200], fEnd: [30, 100], decay: [0.10, 1.20], click: [0.0, 1.0], drive: [0.5, 4.0], lpStrength: [0.0, 0.6] },
  snare: { body: [120, 320], noiseMix: [0.0, 1.0], decay: [0.08, 0.50], hpAlpha: [0.10, 0.95], drive: [0.5, 4.0] },
  hihat: { decay: [0.03, 0.20], decayOpen: [0.10, 0.50], hpAlpha: [0.20, 0.95], ringMix: [0.0, 0.7] },
  tom:   { decay: [0.15, 0.80], drive: [0.5, 4.0], lpStrength: [0.0, 0.6] },
};

/** Built-in kit presets. Each must contain ALL keys from KIT_PARAM_SCHEMA. */
export const SYNTH_KIT_PRESETS = {
  "synth-808": {
    kick:  { f0: 90,  fEnd: 45, decay: 0.55, click: 0.15, drive: 1.0, lpStrength: 0.0 },
    snare: { body: 180, noiseMix: 0.6, decay: 0.22, hpAlpha: 0.50, drive: 1.0 },
    hihat: { decay: 0.08, decayOpen: 0.30, hpAlpha: 0.85, ringMix: 0.10 },
    tom:   { decay: 0.45, drive: 1.0, lpStrength: 0.0 },
  },
  "synth-909": {
    kick:  { f0: 130, fEnd: 55, decay: 0.30, click: 0.35, drive: 1.2, lpStrength: 0.0 },
    snare: { body: 220, noiseMix: 0.7, decay: 0.18, hpAlpha: 0.55, drive: 1.1 },
    hihat: { decay: 0.07, decayOpen: 0.25, hpAlpha: 0.82, ringMix: 0.15 },
    tom:   { decay: 0.32, drive: 1.2, lpStrength: 0.0 },
  },
  "synth-lofi": {
    kick:  { f0: 95,  fEnd: 60, decay: 0.32, click: 0.10, drive: 0.9, lpStrength: 0.35 },
    snare: { body: 170, noiseMix: 0.5, decay: 0.20, hpAlpha: 0.20, drive: 0.9 },
    hihat: { decay: 0.06, decayOpen: 0.20, hpAlpha: 0.45, ringMix: 0.05 },
    tom:   { decay: 0.30, drive: 0.9, lpStrength: 0.45 },
  },
  "synth-dnb": {
    kick:  { f0: 140, fEnd: 50, decay: 0.28, click: 0.45, drive: 1.6, lpStrength: 0.0 },
    snare: { body: 280, noiseMix: 0.8, decay: 0.16, hpAlpha: 0.65, drive: 1.4 },
    hihat: { decay: 0.08, decayOpen: 0.28, hpAlpha: 0.88, ringMix: 0.20 },
    tom:   { decay: 0.28, drive: 1.4, lpStrength: 0.0 },
  },
  "synth-industrial": {
    kick:  { f0: 110, fEnd: 55, decay: 0.40, click: 0.55, drive: 2.4, lpStrength: 0.0 },
    snare: { body: 240, noiseMix: 0.7, decay: 0.25, hpAlpha: 0.45, drive: 2.2 },
    hihat: { decay: 0.10, decayOpen: 0.30, hpAlpha: 0.70, ringMix: 0.40 },
    tom:   { decay: 0.40, drive: 2.2, lpStrength: 0.0 },
  },
  "synth-disco": {
    kick:  { f0: 120, fEnd: 60, decay: 0.35, click: 0.25, drive: 1.0, lpStrength: 0.25 },
    snare: { body: 200, noiseMix: 0.55, decay: 0.20, hpAlpha: 0.45, drive: 1.0 },
    hihat: { decay: 0.07, decayOpen: 0.22, hpAlpha: 0.75, ringMix: 0.12 },
    tom:   { decay: 0.35, drive: 1.0, lpStrength: 0.25 },
  },
};

export const SYNTH_DRUM_KITS = Object.keys(SYNTH_KIT_PRESETS);

export function isSynthDrumKit(kitId) {
  return typeof kitId === "string" && kitId.startsWith("synth-");
}

/** Validate + fill missing params with sensible defaults from the schema midpoint. */
function ensureParams(p = {}) {
  const out = {};
  for (const voice of Object.keys(KIT_PARAM_SCHEMA)) {
    out[voice] = {};
    const schema = KIT_PARAM_SCHEMA[voice];
    const src = p[voice] || {};
    for (const key of Object.keys(schema)) {
      const [min, max] = schema[key];
      const mid = (min + max) / 2;
      const v = typeof src[key] === "number" ? src[key] : mid;
      out[voice][key] = Math.max(min, Math.min(max, v));
    }
  }
  return out;
}

/** Render a full kit from a params struct. Cache-keyed by the param hash so
 *  two slots referencing the same params share the same buffers. */
export function renderKitWithParams(ctx, params, cacheKey) {
  const safeParams = ensureParams(params);
  const key = `${cacheKey || JSON.stringify(safeParams)}@${ctx.sampleRate}`;
  if (cache.has(key)) return cache.get(key);

  const promise = (async () => ({
    kick:  renderKick(ctx, safeParams.kick),
    snare: renderSnare(ctx, safeParams.snare),
    hihat: renderHihat(ctx, safeParams.hihat, false),
    tom1:  renderTom(ctx, safeParams.tom, 1),
    tom2:  renderTom(ctx, safeParams.tom, 2),
    tom3:  renderTom(ctx, safeParams.tom, 3),
  }))();
  cache.set(key, promise);
  return promise;
}

/** Load a built-in synth kit by id. */
export function loadSynthDrumKit(ctx, kitId) {
  const params = SYNTH_KIT_PRESETS[kitId];
  if (!params) throw new Error(`Unknown synth kit: ${kitId}`);
  return renderKitWithParams(ctx, params, kitId);
}

/** Invalidate the render cache for a single kit (used when its params change). */
export function invalidateKitCache(cacheKey) {
  for (const k of cache.keys()) {
    if (k.startsWith(`${cacheKey}@`)) cache.delete(k);
  }
}
